import React from 'react';
import { calculateSLAStatus } from '../utils/helpers';
import { STAGES, SLA_STATUS } from '../utils/constants';
import { useLiveFeed } from './live/LiveFeedProvider';
import TickerNumber from './live/TickerNumber';
import Sparkline from './live/Sparkline';

const MetricBox = ({ label, value, colorClass = 'text-white' }) => (
  <div className="relative border border-white/10 p-6 flex flex-col justify-between hover:border-white/30 transition-colors bg-white/[0.02]">
    <span className="font-mono-tech text-[10px] text-neutral-500 uppercase tracking-[0.2em] mb-4">
      {label}
    </span>
    <span className={`text-5xl xl:text-7xl font-mono-tech font-bold tracking-tighter ${colorClass}`}>
      {value}
    </span>
  </div>
);

const AnimatedMetricBox = ({ label, tickerNode, sparkValues, colorClass = 'text-white' }) => (
  <div className="relative border border-white/10 p-6 flex flex-col justify-between hover:border-white/30 transition-colors bg-white/[0.02] overflow-hidden">
    <span className="font-mono-tech text-[10px] text-neutral-500 uppercase tracking-[0.2em] mb-4">
      {label}
    </span>
    <span className={`text-4xl xl:text-6xl font-mono-tech font-bold tracking-tighter ${colorClass}`}>
      {tickerNode}
    </span>
    <div className={`absolute left-0 right-0 bottom-0 h-[6px] ${colorClass} opacity-60`}>
      <Sparkline values={sparkValues} width={200} height={6} />
    </div>
  </div>
);

const MetricsHeader = ({ vehicles, currentTime }) => {
  const { revenueToday, revenuePerHour, throughputPerMin } = useLiveFeed();

  const totalVehicles = vehicles.length;
  let breached = 0;
  let warning = 0;
  let onTrack = 0;

  vehicles.forEach((v) => {
    const stage = STAGES.find((s) => s.id === v.stageId);
    if (stage) {
      const status = calculateSLAStatus(v.entryTime, stage.slaMinutes, currentTime);
      if (status === SLA_STATUS.BREACHED) breached++;
      else if (status === SLA_STATUS.WARNING) warning++;
      else onTrack++;
    }
  });

  const efficiency = totalVehicles > 0 ? Math.round((onTrack / totalVehicles) * 100) : 100;
  const currentThru = throughputPerMin[throughputPerMin.length - 1] ?? 0;
  const throughputPerHour = Math.round(currentThru * 60 * 10) / 10;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-10 flex-shrink-0">
      <MetricBox label="Active_Units" value={totalVehicles.toString().padStart(2, '0')} />
      <MetricBox
        label="SLA_Breached"
        value={breached.toString().padStart(2, '0')}
        colorClass={breached > 0 ? 'text-[#D71921]' : 'text-white'}
      />
      <MetricBox
        label="In_Warning"
        value={warning.toString().padStart(2, '0')}
        colorClass={warning > 0 ? 'text-[#FFB800]' : 'text-white'}
      />
      <MetricBox label="Efficiency_Rating" value={`${efficiency}%`} colorClass="text-[#00FF41]" />
      <AnimatedMetricBox
        label="Revenue_Today"
        colorClass="text-white"
        tickerNode={<TickerNumber value={revenueToday} digits={6} prefix="R " />}
        sparkValues={revenuePerHour}
      />
      <AnimatedMetricBox
        label="Throughput_/Hr"
        colorClass="text-[#00FF41]"
        tickerNode={<><TickerNumber value={Math.round(throughputPerHour * 10)} digits={3} />/h</>}
        sparkValues={throughputPerMin}
      />
    </div>
  );
};

export default MetricsHeader;
